<template>
  <div class="strategy-detail-page">
    <div class="container article-container">
      <!-- 面包屑导航 -->
      <nav class="breadcrumb">
        <a href="#/strategies" class="back-link">
          ← {{ t('strategies.backToList') }}
        </a>
      </nav>

      <article v-if="article" class="article-content animate-fade-in-up">
        <header class="article-header">
          <div class="meta">
            <span class="category-tag">{{ t(`games.${article.category}.title`) }}</span>
            <span class="date">{{ formatDate(article.date) }}</span>
            <span class="read-time">{{ article.readTime }}</span>
          </div>
          <h1 class="article-title">{{ article.title }}</h1>
        </header>

        <div class="article-body" v-html="article.content"></div>

        <!-- 底部 CTA -->
        <div class="article-cta">
          <p>{{ t('strategies.readyToPlay') }}</p>
          <a :href="'#' + article.relatedGameLink" class="cta-button">
            {{ article.relatedGameText }}
          </a>
        </div>
      </article>

      <div v-else class="not-found">
        <h2>{{ t('strategies.notFound') }}</h2>
        <a href="#/strategies" class="back-link">{{ t('strategies.backToList') }}</a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from '@/utils/i18n'
import { strategies } from '@/data/strategies'

const { t, currentLang } = useI18n()

const articleId = ref('')

onMounted(() => {
  // Parse ID from hash: #/strategies/sudoku-beginner-guide
  const hash = window.location.hash
  const parts = hash.split('/')
  if (parts.length > 2) {
    articleId.value = parts[parts.length - 1]
  }
})

const article = computed(() => {
  const rawArticle = strategies.find(s => s.id === articleId.value)
  if (!rawArticle) return null

  const suffix = currentLang.value === 'zh' ? '_zh' : (currentLang.value === 'fr' ? '_fr' : '_en')
  
  return {
    ...rawArticle,
    title: rawArticle[`title${suffix}`] || rawArticle.title_en,
    summary: rawArticle[`summary${suffix}`] || rawArticle.summary_en,
    readTime: rawArticle[`readTime${suffix}`] || rawArticle.readTime_en,
    content: rawArticle[`content${suffix}`] || rawArticle.content_en,
    relatedGameText: rawArticle[`relatedGameText${suffix}`] || rawArticle.relatedGameText_en
  }
})

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat(currentLang.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date)
}
</script>

<style scoped>
.strategy-detail-page {
  padding: var(--space-4xl) 0;
  background-color: var(--color-bg-white);
  min-height: 80vh;
}

.article-container {
  max-width: 800px;
  margin: 0 auto;
}

.breadcrumb {
  margin-bottom: var(--space-2xl);
}

.back-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  transition: color var(--transition-base) var(--ease-in-out);
}

.back-link:hover {
  color: var(--color-primary-orange);
}

.article-header {
  margin-bottom: var(--space-3xl);
  text-align: center;
}

.meta {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
}

.category-tag {
  background: rgba(242, 78, 30, 0.1);
  color: var(--color-primary-orange);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
}

.article-title {
  font-size: clamp(32px, 5vw, 48px);
  line-height: 1.2;
  color: var(--color-text-primary);
  margin-bottom: var(--space-md);
}

.article-body {
  font-size: 18px;
  line-height: 1.8;
  color: var(--color-text-primary);
}

.article-body :deep(h2) {
  font-size: 28px;
  margin-top: var(--space-3xl);
  margin-bottom: var(--space-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.article-body :deep(h3) {
  font-size: 22px;
  margin-top: var(--space-2xl);
  margin-bottom: var(--space-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.article-body :deep(p) {
  margin-bottom: var(--space-lg);
  opacity: 0.9;
}

.article-body :deep(ul), .article-body :deep(ol) {
  margin-bottom: var(--space-lg);
  padding-left: var(--space-xl);
}

.article-body :deep(li) {
  margin-bottom: var(--space-xs);
}

.article-body :deep(strong) {
  color: var(--color-primary-orange);
}

.article-body :deep(hr) {
  border: 0;
  height: 1px;
  background: var(--color-border-light);
  margin: var(--space-3xl) 0;
}

.article-cta {
  margin-top: var(--space-4xl);
  padding: var(--space-3xl);
  background: var(--color-bg-subtle);
  border-radius: var(--radius-lg);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-lg);
}

.article-cta p {
  font-size: var(--font-size-h4);
  font-weight: var(--font-weight-medium);
  margin: 0;
}

.cta-button {
  background: var(--color-primary-orange);
  color: white;
  padding: var(--space-md) var(--space-2xl);
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: var(--font-weight-bold);
  transition: all var(--transition-base) var(--ease-in-out);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
  background: var(--color-primary-gold);
}

@media (max-width: 640px) {
  .article-title {
    font-size: 32px;
  }
}
</style>
